/* eslint-disable complexity */
import {
  differenceInCalendarDays,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  isFuture,
} from 'date-fns';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const JUST_NOW_MINUTE_COUNT = 5;

type TimeUnit = 'year' | 'month' | 'week' | 'day' | 'hour' | 'minute';

interface RelativeTimeProps {
  value: number;
  unit: TimeUnit;
  inFuture: boolean;
  sentenceCase: boolean;
}

export interface TimestampRelativeFormatOptions {
  sentenceCase?: boolean;
}

const RelativeTime = ({ value, unit, inFuture, sentenceCase }: RelativeTimeProps) => {
  const intl = useIntl();
  const relativeString = intl.formatRelativeTime(inFuture ? value : -value, unit, {
    numeric: 'auto'
  });

  const formattedRelativeTime = sentenceCase
    ? relativeString
    : relativeString.charAt(0).toUpperCase() + relativeString.slice(1);

  return <span>{formattedRelativeTime}</span>;
};

export const getTimestampRelativeFormat = (
  timestamp: string | Date | number,
  date: string | Date | number = Date.now(),
  justNowMinuteCount: number = JUST_NOW_MINUTE_COUNT,
  excludeTime = false,
  options: TimestampRelativeFormatOptions = {}
): JSX.Element => {
  const dateNow = new Date(date);
  const dateBase = new Date(timestamp);
  const inFuture = isFuture(dateBase);
  const { sentenceCase = false } = options;

  const yearsDifference = Math.abs(differenceInYears(dateNow, dateBase));
  if (yearsDifference > 0) {
    return <RelativeTime value={yearsDifference} unit="year" inFuture={inFuture} sentenceCase={sentenceCase} />;
  }

  const monthsDifference = Math.abs(differenceInMonths(dateNow, dateBase));
  if (monthsDifference > 0) {
    return <RelativeTime value={monthsDifference} unit="month" inFuture={inFuture} sentenceCase={sentenceCase} />;
  }

  const weeksDifference = Math.abs(differenceInWeeks(dateNow, dateBase));
  if (weeksDifference > 0) {
    return <RelativeTime value={weeksDifference} unit="week" inFuture={inFuture} sentenceCase={sentenceCase} />;
  }

  const calendarDayDifference = differenceInCalendarDays(dateBase, dateNow);

  if (excludeTime) {
    if (calendarDayDifference === 0) {
      return <FormattedMessage id="common.today" defaultMessage="Today" />;
    }

    if (calendarDayDifference === 1) {
      return <FormattedMessage id="common.tomorrow" defaultMessage="Tomorrow" />;
    }

    if (calendarDayDifference === -1) {
      return <FormattedMessage id="common.yesterday" defaultMessage="Yesterday" />;
    }

    return (
      <RelativeTime
        value={Math.abs(calendarDayDifference)}
        unit="day"
        inFuture={inFuture}
        sentenceCase={sentenceCase}
      />
    );
  }

  const daysDifference = Math.abs(differenceInDays(dateNow, dateBase));
  if (daysDifference > 0) {
    return <RelativeTime value={daysDifference} unit="day" inFuture={inFuture} sentenceCase={sentenceCase} />;
  }

  const hoursDifference = Math.abs(differenceInHours(dateNow, dateBase));
  if (hoursDifference > 0) {
    return <RelativeTime value={hoursDifference} unit="hour" inFuture={inFuture} sentenceCase={sentenceCase} />;
  }

  const minutesDifference = Math.abs(differenceInMinutes(dateNow, dateBase));
  if (minutesDifference > justNowMinuteCount || (inFuture && minutesDifference > 0)) {
    return <RelativeTime value={minutesDifference} unit="minute" inFuture={inFuture} sentenceCase={sentenceCase} />;
  }

  return sentenceCase ? (
    <FormattedMessage id="common.just_now_in_sentence" defaultMessage="just now" />
  ) : (
    <FormattedMessage id="common.just_now" defaultMessage="Just now" />
  );
};
